import React from 'react';
import {
  ActionButton,
  Checkbox,
  CommandButton, Separator, Stack, Text,
} from '@fluentui/react';
import StackTokens from '../shared/StackTokens';
import Theme from '../shared/Theme';
import Store from '../shared/state/Store';
import UiReducer from '../shared/state/UiReducer';
import AppsApi from '../shared/util/AppsApi';
import EndUserAssignDialog from './EndUserAssignDialog';
import PlaceholderError from '../shared/PlaceholderError';
import User from '../shared/User';

export default () => {
  const [globalState, dispatch] = Store.useStore();
  const [emailsToDelete, setEmailsToDelete] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      await User.fetchAccounts({ dispatch, displayErrors: false });
    })();
  }, []);

  const accountInfo = UiReducer.getGenericUiAttribute(globalState, UiReducer.Keys.AccountInfo);
  const accountInfoServerError = UiReducer.getGenericUiAttribute(globalState, UiReducer.Keys.AccountInfoServerError);

  if (accountInfoServerError) {
    return (<PlaceholderError message="Server is unavailable at this time, please try again later." />);
  }

  if (!accountInfo || Object.keys(accountInfo).length === 0) {
    return (<PlaceholderError message="Loading account data..." />);
  }

  const subscriptionAccount = accountInfo.primaryAccount;
  const globalEndUsers = subscriptionAccount.subscription.globalEndUsers || [];
  const { maxUsersTotal } = subscriptionAccount.subscription;
  const totalEndUsers = subscriptionAccount.subscription.totalEndUsers || 0;
  const availableAssignments = maxUsersTotal - (totalEndUsers + globalEndUsers.length);

  return (
    <Stack vertical>

      <Stack horizontal tokens={StackTokens.spacing}>
        <CommandButton
          text="Add"
          iconProps={{
            iconName: 'Add',
          }}
          className={[Theme.styles.outlineControlButton, Theme.styles.commandButton].join(' ')}
          onClick={async () => {
            dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.GlobalEndUserAssignmentDialogOpen, true));
          }}
        />

        <CommandButton
          text="Unassign"
          disabled={emailsToDelete.length === 0}
          iconProps={{
            iconName: 'Clear',
          }}
          className={[Theme.styles.outlineControlButton, Theme.styles.commandButton].join(' ')}
          onClick={async () => {
            const toRemove = new Set(emailsToDelete);
            const updatedGlobalEndUsers = globalEndUsers.filter((email) => !toRemove.has(email));

            const responseJson = await AppsApi.updateSubscription({ globalEndUsers: updatedGlobalEndUsers });
            if (responseJson.responseStatus !== 200) {
              dispatch(UiReducer.uiSetErrors(responseJson.messages));
            } else {
              // Update the accountInfo with the validated and normalized list
              const updatedAccountInfo = {
                ...accountInfo,
                primaryAccount: {
                  ...subscriptionAccount,
                  subscription: {
                    ...subscriptionAccount.subscription,
                    globalEndUsers: responseJson.globalEndUsers,
                  },
                },
              };
              dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.AccountInfo, updatedAccountInfo));
              setEmailsToDelete([]);
            }
          }}
        />
      </Stack>

      <Separator styles={Theme.styleObjects.separatorSmallMargin} />

      <Text block style={{ margin: '10px 0 0 0' }}>
        All users named in this list will have end-user access to all apps on this subscription, including any apps owned by creators assigned a key from this subscription.
      </Text>

      <Text block style={{ margin: '10px 0 0 0' }}>
        Assignments available:
        {' '}
        {availableAssignments}
        {' '}
        (of maximum
        {' '}
        {maxUsersTotal}
        )
      </Text>

      <Text block>
        <ActionButton
          onClick={() => {
            const account = User.getEffectiveSubscriptionAccount(globalState);
            if (!account) {
              return;
            }
            const { subscription } = account;
            const rules = subscription.endUserAssignmentRules;
            let ruleSelfEnabled = false;
            let ruleTeamEnabled = false;
            let ruleAnyoneEnabled = false;
            const ruleDomainsUncapped = [];
            const ruleDomainsCapped = [];
            rules.forEach((rule) => {
              if (rule.type === 'entity' && rule.target === 'self') {
                ruleSelfEnabled = true;
              }
              if (rule.type === 'entity' && rule.target === 'team') {
                ruleTeamEnabled = true;
              }
              if (rule.type === 'entity' && rule.target === 'anyone') {
                ruleAnyoneEnabled = true;
              }
              if (rule.type === 'domain_uncapped') {
                ruleDomainsUncapped.push(rule.target);
              }
              if (rule.type === 'domain_capped') {
                ruleDomainsCapped.push(rule.target);
              }
            });
            const team = User.getAccountInfo(globalState)?.team || [];

            dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.GeneralSimpleMessageModal, {
              title: 'End-user assignment rules',
              content: (
                <>
                  {ruleSelfEnabled && <Text block>+ You can assign yourself as an end-user</Text>}
                  {ruleTeamEnabled && team.length > 0 && (
                    <div>
                      <Text block>+ You can assign anyone from your team:</Text>
                      <ul>{team.map((teamMember) => <li key={teamMember}>{teamMember}</li>)}</ul>
                    </div>
                  )}
                  {ruleAnyoneEnabled && <Text block>+ Anyone</Text>}
                  {ruleDomainsCapped.length > 0 && (
                    <div>
                      <Text block>+ You can assign any user from the following domains:</Text>
                      <ul>{ruleDomainsCapped.map((domain) => <li key={domain}>{domain}</li>)}</ul>
                    </div>
                  )}
                  {ruleDomainsUncapped.length > 0 && (
                    <div>
                      <Text block>+ You can enable domain-wide blanket access for anyone from the following domains.</Text>
                      <Text block>Anyone registered in that domain would be able to access your app as a user without being named explicitly.</Text>
                      <Text block>Such access can be configured for individual apps separately.</Text>
                      <ul>{ruleDomainsUncapped.map((domain) => <li key={domain}>{domain}</li>)}</ul>
                    </div>
                  )}
                </>
              ),
            }));
          }}
          text="Show end-user assignment rules for this subscription"
          className={[Theme.styles.linkButton, Theme.styles.outlineControlButton].join(' ')}
        />
      </Text>

      <div>
        <table className={Theme.styles.table} style={(globalEndUsers.length === 0) ? { display: 'none' } : undefined}>
          <thead>
            <tr>
              <th>
                <Checkbox
                  className={Theme.styles.tableRowCheckbox}
                  checked={emailsToDelete.length === globalEndUsers.length && globalEndUsers.length > 0}
                  indeterminate={emailsToDelete.length > 0 && emailsToDelete.length < globalEndUsers.length}
                  onChange={() => {
                    let updated = [];
                    if (emailsToDelete.length < globalEndUsers.length) {
                      // If not all checked, check all
                      updated = globalEndUsers.map((email) => email);
                    } else {
                      // If all checked, uncheck all
                    }
                    setEmailsToDelete(updated);
                  }}
                />
              </th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {
            globalEndUsers.map((email) => (
              <tr key={email}>
                <td>
                  <Checkbox
                    className={Theme.styles.tableRowCheckbox}
                    checked={emailsToDelete.includes(email)}
                    onChange={() => {
                      const index = emailsToDelete.indexOf(email);
                      const updated = [...emailsToDelete];
                      if (index >= 0) {
                        updated.splice(index, 1);
                      } else {
                        updated.push(email);
                      }
                      setEmailsToDelete(updated);
                    }}
                  />
                </td>
                <td>{email}</td>
              </tr>
            ))
            }
          </tbody>
        </table>
      </div>

      <EndUserAssignDialog
        primaryCallback={async (emailsRaw) => {
          let emails = [];
          if (emailsRaw.trim() !== '') {
            emails = emailsRaw.split('\n');
          }

          const actualEmails = globalEndUsers;

          let noChanges = false;

          if (emails.length === 0 && actualEmails.length === 0) {
            // No change
            noChanges = true;
          }

          if (!noChanges) {
            // Check that there was an actual change
            const newItems = new Set(emails);
            if (newItems.size === actualEmails.length && actualEmails.every((item) => newItems.has(item))) {
              // Same set of emails, do nothing
              noChanges = true;
            }
          }

          if (!noChanges) {
            // Only submit API request if list was actually changed
            const responseJson = await AppsApi.updateSubscription({ globalEndUsers: emails });
            if (responseJson.responseStatus !== 200) {
              dispatch(UiReducer.uiSetErrors(responseJson.messages));
              return;
            }

            // Update the accountInfo with the validated and normalized list
            const updatedAccountInfo = {
              ...accountInfo,
              primaryAccount: {
                ...subscriptionAccount,
                subscription: {
                  ...subscriptionAccount.subscription,
                  globalEndUsers: responseJson.globalEndUsers,
                },
              },
            };
            dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.AccountInfo, updatedAccountInfo));
          }

          dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.GlobalEndUserAssignmentDialogOpen, false));
          setEmailsToDelete([]);
        }}
      />
    </Stack>
  );
};
